// OrderSummaryChart.jsx
//
// Chart view for the Distributor Orders → Summary tab. A Line / Stacked-Bar
// toggle, recharts-driven render, custom tooltip + compact legend. One series
// per region; when the parent's Region filter is set, only that series shows.
// An internal size toggle scopes totals to All Cases or any one of the
// account's own bottle sizes (see utils/sizeGroups.js).
//
// Read-only — receives the same shipment rows the grid uses.

import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Label, LabelList,
} from "recharts";
import { ALL_CASES_ID, skuSize } from "../utils/sizeGroups";
import "./ForecastChart.css";

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/* Series identity. Eight global --chart-N tokens (styles.css), which carry a
   validated per-theme step — the old form here was 15 raw hex that no theme
   could reach, so the series never changed between light and dark, and slot 1
   (#f5a623) was a fourth near-brand orange sitting next to --o-fill and --warn.
   Referenced as var() rather than resolved in JS so the theme toggle repaints
   without a re-render.
   ⚠️ Eight, not fifteen, and NOT cycled: a 9th size would silently reuse slot 1
   and two series would read as the same thing. seriesColor() returns null past
   eight so the caller can fold the tail into "Other" — see EXTRA_COLOR. */
const COLORS = [
  "var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)",
  "var(--chart-5)", "var(--chart-6)", "var(--chart-7)", "var(--chart-8)",
];
/* Ninth and beyond. Neutral on purpose: it reads as "not one of the named
   series" instead of impersonating one. */
const EXTRA_COLOR = "var(--muted)";
const seriesColor = (i) => COLORS[i] ?? EXTRA_COLOR;

// All Cases first, then one scope per size the account sells.
function buildSizeScopes(sizes = []) {
  const seen = new Set();
  const scopes = [{ id: ALL_CASES_ID, label: "All Cases", sizeMatch: null }];
  for (const raw of sizes) {
    const name = String(raw ?? "").trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    scopes.push({ id: name, label: name, sizeMatch: name });
  }
  return scopes;
}

function CompactLegend({ payload, chartType }) {
  if (!payload?.length) return null;
  const useTwoCols = payload.length > 10;
  return (
    <ul style={{
      listStyle: "none", margin: 0, padding: 0,
      columnCount: useTwoCols ? 2 : 1, columnGap: 14,
    }}>
      {payload.map((entry, i) => (
        <li key={`${entry.value}-${i}`} style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "2px 0", breakInside: "avoid", fontSize: 12,
          color: "var(--text)", lineHeight: "20px",
        }}>
          <span style={{
            display: "inline-block",
            width:  chartType === "bar" ? 11 : 16,
            height: chartType === "bar" ? 11 : 3,
            background: entry.color,
            borderRadius: chartType === "bar" ? 2 : 0,
            flexShrink: 0,
          }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {entry.value}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function OrderSummaryChart({ orderRows, stateRegionMap, regions, periods, periodMode, fRegion, sizes = [] }) {
  const [chartType, setChartType] = useState("bar");
  const [sizeScope, setSizeScope] = useState(ALL_CASES_ID);
  const SIZE_SCOPES = useMemo(() => buildSizeScopes(sizes), [sizes]);

  const scopeDef = SIZE_SCOPES.find(s => s.id === sizeScope) || SIZE_SCOPES[0];

  const displayRegions = fRegion ? [fRegion] : regions;

  const { chartData, hasData } = useMemo(() => {
    const bucket = new Map();
    for (const item of orderRows) {
      const order = item.order;
      if (!order || !order.pickup_date) continue;
      const region = stateRegionMap[order.state];
      if (!region) continue;
      if (fRegion && region !== fRegion) continue;
      if (scopeDef.sizeMatch && skuSize(item.sku_name) !== scopeDef.sizeMatch) continue;
      const ym  = String(order.pickup_date).slice(0, 7);
      const qty = Number(item.quantity) || 0;
      if (qty <= 0) continue;
      const k = `${region}|${ym}`;
      bucket.set(k, (bucket.get(k) || 0) + qty);
    }

    const periodSum = (region, period) =>
      period.members.reduce((s, m) => {
        const ym = `${m.year}-${String(m.mnum).padStart(2, "0")}`;
        return s + (bucket.get(`${region}|${ym}`) || 0);
      }, 0);

    const allYears = new Set(periods.map(p => p.members[0]?.year).filter(Boolean));
    const sameYear = allYears.size <= 1;
    const labelFor = (p) => {
      if (periodMode === "quarter") {
        const yy = String(p.year ?? p.members[0]?.year ?? "").slice(2);
        return `Q${p.quarter} ${yy}`;
      }
      const m = p.members[0];
      return sameYear ? MONTH_ABBR[m.mnum - 1] : `${MONTH_ABBR[m.mnum - 1]} ${String(m.year).slice(2)}`;
    };

    const data = periods.map(p => {
      const row = { month: labelFor(p) };
      let total = 0;
      for (const region of displayRegions) {
        const v = periodSum(region, p);
        row[region] = v;
        total += v;
      }
      row._total = total;
      return row;
    });

    const activeRegions = displayRegions.filter(r => data.some(d => (d[r] ?? 0) > 0));
    data.forEach(d => { d._activeTotal = activeRegions.reduce((s, r) => s + (d[r] ?? 0), 0); });

    return { chartData: data, hasData: data.some(d => activeRegions.some(r => d[r] > 0)) };
  }, [orderRows, stateRegionMap, fRegion, scopeDef.sizeMatch, periods, periodMode, displayRegions]);

  const seriesKeys = useMemo(() => {
    return displayRegions.filter(r => chartData.some(d => (d[r] ?? 0) > 0));
  }, [chartData, displayRegions]);

  if (!orderRows || orderRows.length === 0) {
    return (
      <div className="chart-prompt">
        <div className="chart-prompt-icon">📊</div>
        <div className="chart-prompt-text">No shipments in the current range.</div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-label">{label}</div>
        <div className="chart-tooltip-total">
          Total: <strong>{Math.round(total).toLocaleString()}</strong> cases
        </div>
        <div className="chart-tooltip-divider" />
        {[...payload].reverse().map(p => {
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) + "%" : "—";
          return (
            <div key={p.dataKey} className="chart-tooltip-row">
              <span className="chart-tooltip-swatch" style={{ background: p.color }} />
              <span className="chart-tooltip-name">{p.dataKey}</span>
              <span className="chart-tooltip-value">{p.value > 0 ? Math.round(p.value).toLocaleString() : "—"}</span>
              <span className="chart-tooltip-pct">{p.value > 0 ? pct : ""}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const BarTotalLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    const txt = value >= 1000
      ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
      : Math.round(value).toLocaleString();
    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle" fill="var(--text)" fontSize={11} fontWeight={600}>
        {txt}
      </text>
    );
  };

  const yFmt = v => v >= 1000
    ? `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
    : String(v);
  const sharedProps = { data: chartData, margin: { top: 28, right: 16, left: 8, bottom: 4 } };

  /* Chart CHROME (axes, grid, legend, cursor) is theme-aware — .chart-area is a
     themed surface now, and this was raw light-theme slate that went invisible
     on the dark canvas. The series palette is theme-aware too as of 2026-08-08
     (backlog 405): "qualitative data encoding, not theme chrome" was the reason
     it was left as literals, but a series colour still has to be legible on the
     surface it is drawn on, and that surface changes. */
  const xAxis = (
    <XAxis dataKey="month" tick={{ fill: "var(--muted)", fontSize: 12 }}
      axisLine={{ stroke: "var(--border)" }} tickLine={false} />
  );
  const yAxis = (
    <YAxis tickFormatter={yFmt} tick={{ fill: "var(--muted)", fontSize: 11 }}
      axisLine={false} tickLine={false} width={64}>
      <Label value="Cases" angle={-90} position="insideLeft" offset={12}
        style={{ fill: "var(--muted)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }} />
    </YAxis>
  );
  const grid    = <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />;
  const tooltip = <Tooltip content={<CustomTooltip />} cursor={{ fill: "var(--hairline)" }} />;
  const legend  = (
    <Legend layout="vertical" align="right" verticalAlign="middle"
      wrapperStyle={{
        fontSize: 12, color: "var(--text)", paddingLeft: 16, lineHeight: "22px",
        maxHeight: "92%", overflowY: "auto", top: "4%",
      }}
      content={<CompactLegend chartType={chartType} />} />
  );

  return (
    <div className="chart-wrap">
      <div className="chart-controls">
        <div className="chart-type-toggle">
          <button
            className={`chart-type-btn${chartType === "line" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("line")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Line
          </button>
          <button
            className={`chart-type-btn${chartType === "bar" ? " chart-type-btn-active" : ""}`}
            onClick={() => setChartType("bar")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="4" height="18"/><rect x="10" y="8" width="4" height="13"/><rect x="17" y="13" width="4" height="8"/>
            </svg>
            Stacked Bar
          </button>
        </div>

        <div className="chart-group-by">
          <span className="chart-group-label">Size</span>
          <select
            className="chart-group-select"
            value={sizeScope}
            onChange={e => setSizeScope(e.target.value)}
          >
            {SIZE_SCOPES.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {!hasData && <span className="chart-no-data">No data for current filters</span>}
      </div>

      <div className="chart-area">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "line" ? (
            <LineChart {...sharedProps}>
              {grid}{xAxis}{yAxis}{tooltip}{legend}
              {seriesKeys.map((region, i) => (
                <Line key={region} type="linear" dataKey={region}
                  stroke={seriesColor(i)} strokeWidth={2}
                  dot={{ r: 3, fill: seriesColor(i), strokeWidth: 0 }}
                  activeDot={{ r: 5 }} connectNulls />
              ))}
            </LineChart>
          ) : (
            <BarChart {...sharedProps}>
              {grid}{xAxis}{yAxis}{tooltip}{legend}
              {seriesKeys.map((region, i) => (
                <Bar key={region} dataKey={region} stackId="stack"
                  fill={seriesColor(i)}
                  /* 2px surface-coloured gap between stacked segments, so two
                     adjacent fills never touch and read as one block. */
                  stroke="var(--surface)" strokeWidth={2}
                  radius={i === seriesKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}>
                  {i === seriesKeys.length - 1 && (
                    <LabelList dataKey="_total" content={<BarTotalLabel />} />
                  )}
                </Bar>
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
